// ============================================================================
// WON TO WORK ORDER — the honestly dormant panel.
// ----------------------------------------------------------------------------
// This panel is designed and shipped with no data behind it on purpose. It is
// the reference implementation of the board's honesty rule: a figure is only
// drawn when something in the system actually recorded the event it claims to
// measure, and nothing records a work-order conversion today.
//
// The near miss that this file exists to refuse: `work_order_drafts` carries
// `status`, `reviewed_at` and `superseded_at`, so a conversion rate could be
// computed from it in an afternoon. It would be wrong. Those columns describe
// the life of a DOCUMENT, and a draft is generated whether or not anyone ever
// stands on the site. Reporting draft generation as conversion would put a
// confident number in front of the operator that no job start backs, and a
// wrong number is worse than a dashed border, because the operator would act
// on it.
//
// So the panel occupies its slot and says what it will show and what has to
// happen first. It is deliberately slim and full width: a quiet strip at the
// foot of the board reads as an announcement about the future, which is what
// it is, rather than as a measurement that failed to load.
// ============================================================================

import type { PanelDef, PanelSeries, PanelReadiness } from '../registry';

/**
 * Section 7 of the spec, verbatim. It names the three things that stay dark,
 * so the operator learns what is missing rather than only that something is.
 */
const BODY =
    'Nothing records a work-order conversion today, so conversion lag, rate by trade '
    + 'and delivered against quoted all stay dark.';

/** Section 3.4 of the spec, verbatim. The stamp names the source, or its absence. */
const STAMP = 'no source yet · work_order_drafts records draft generation, not a site start';

/** Section 3.10 of the spec: the single event that would switch this panel on. */
const ACTIVATION = 'A conversion event (new column or table) written when a won quote starts on site.';

// Every member below takes no arguments even though the contract offers a
// context, a series and print dimensions. That is the point: this panel reads
// nothing, so a signature that accepted the rows would invite a future agent to
// start counting them.

const workOrderConversion: PanelDef = {
    id: 'work-order-conversion',
    title: 'Won to work order',
    hint: 'designed for data not captured yet',
    group: 'outcome',
    defaultSpan: 12,
    minSpan: 12,
    slim: true,
    mark: 'none',
    // True so that the day a conversion event exists the panel is already
    // period-scoped like the rest of the board, and no "all time" chip has to
    // be explained away.
    respectsPeriod: true,

    compute(): PanelSeries {
        return {
            cells: [],
            total: { count: 0, value: 0 },
            emptySeries: BODY,
        };
    },

    // Not a function of the data, because the gap is in the schema rather than
    // in this tenant's book. A busier quote book would not switch it on.
    readiness(): PanelReadiness {
        return 'dormant';
    },

    activation(): string {
        return ACTIVATION;
    },

    stamp(): string {
        return STAMP;
    },

    // No field is read, so no field is claimed. An empty chip strip is the
    // truthful answer here; naming work_order_drafts columns would imply this
    // panel already consumes them.
    fields: [],

    caveats: [
        'work_order_drafts.status, reviewed_at and superseded_at describe the draft document rather '
        + 'than the job, so counting them would report conversions that no site start backs.',
    ],

    // Headers describe the shape this panel will carry once the event exists.
    // No placeholder row is emitted: a spreadsheet sums whatever rows it is
    // given, and a zero row in a conversion export reads as a measured zero.
    csv() {
        return { headers: ['Measure', 'Jobs', 'Value ex GST'], rows: [] };
    },

    // Nothing to draw. An empty axis on paper implies a measurement was taken
    // and came back flat, so the export omits the mark entirely and keeps the
    // panel title and the stamp, which carry the whole truth of this tile.
    printSvg(): string {
        return '';
    },
};

export default workOrderConversion;
